import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppContext } from '@prisjakt/ui-app-context';
import { defaultProductState } from 'src/areas/product/reducer';
import PriceInformation from '../../price-information';
import { getSortedPrices } from 'src/components/price-list/sorting';
import PriceListHeader from './price-list-header';
import PriceListFooter from './price-list-footer';
import PriceListBody from './price-list-body';
import { IPriceListPanelProps } from './types';
import { getCommonListProps } from './utils';
import { getSplitOfferlist, getListLimits } from './utils/split-list';
import MissingShop from '../../missing-shop';
import {
  PriceDisplayEnum,
  SortOptionEnum,
} from 'src/components/price-list/types';
import { AppState } from 'src/reducers';
import { PriceListFilterContext } from 'src/areas/product/price-list-filter-context';
import { PricePanel, PartnerOffersTitle, H3Text } from './styles';

export const PriceListPanel: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IPriceListPanelProps>
>> = props => {
  const { me, variantGroups, conditions } = props;
  const { market, t } = useAppContext();
  const { selectedSizeVariants } = useContext(PriceListFilterContext);
  const [showMorePrices, setShowMorePrices] = useState(false);
  const [showMoreUnfeaturedPrices, setShowUnfeaturedMorePrices] = useState(
    false
  );
  const sortedPrices = getSortedPrices(
    props.priceDisplay,
    props.currentSorting,
    props.prices
  );
  const prices = getSplitOfferlist(
    sortedPrices,
    props.currentSorting,
    props.priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING,
    Boolean(selectedSizeVariants?.length)
  );
  const commonListProps = getCommonListProps(props);
  const listLimits = getListLimits(prices);
  const currentSort =
    useSelector((state: AppState) => getSortState(state)) ||
    defaultProductState.sorting;

  // Default sorting in UK/FR is Partner offers
  const showSortByFeaturedHeader =
    currentSort === SortOptionEnum.FEATURED &&
    ['fr', 'gb'].includes(market.code);

  return (
    <PricePanel>
      <PriceListHeader
        conditions={conditions}
        variantGroups={variantGroups}
        categoryId={props.category?.id}
      />
      {me?.analyticsAccess?.stores && <MissingShop />}

      {showSortByFeaturedHeader && (
        <PartnerOffersTitle>
          {t('product', 'price-list.header-partner-offers')}
        </PartnerOffersTitle>
      )}

      {Boolean(sortedPrices?.length) && (
        <>
          <PriceListBody
            sortedPrices={prices.mainList}
            showMorePrices={showMorePrices}
            numberOfVisiblePrices={listLimits.mainListLimit}
            {...commonListProps}
          />
          <PriceListFooter
            showProductInformationTexts={props.showProductInformationTexts}
            sortedPrices={prices.mainList}
            showMorePrices={showMorePrices}
            numberOfVisiblePrices={listLimits.mainListLimit}
            setShowMorePrices={setShowMorePrices}
          />
          {Boolean(prices?.secondaryList?.length) && (
            <>
              <H3Text>{t('product', 'splitlist-other.title')}</H3Text>
              <PriceListBody
                sortedPrices={prices!.secondaryList!}
                showMorePrices={showMoreUnfeaturedPrices}
                numberOfVisiblePrices={listLimits.secondaryListLimit}
                {...commonListProps}
              />
              <PriceListFooter
                showProductInformationTexts={props.showProductInformationTexts}
                sortedPrices={prices!.secondaryList!}
                showMorePrices={showMoreUnfeaturedPrices}
                numberOfVisiblePrices={listLimits.secondaryListLimit}
                setShowMorePrices={setShowUnfeaturedMorePrices}
              />
            </>
          )}
        </>
      )}
      {props.storeStatistics && (
        <PriceInformation storeStatistics={props.storeStatistics} />
      )}
    </PricePanel>
  );
};

const getSortState = (state: AppState) => state.areas.product.sorting;
